import type { ButtonSize } from "@replo/design-system/components/button/button-shared";
import type { PendingShopifyIntegrationCookie } from "schemas/cookies";

import * as React from "react";

import { useCurrentWorkspaceId } from "@editor/contexts/WorkspaceDashboardContext";
import { useSubscriptionTier } from "@editor/hooks/subscription";
import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";

import Button from "@replo/design-system/components/button/Button";
import twMerge from "@replo/design-system/utils/twMerge";
import { ArrowUpRight } from "lucide-react";
import { exhaustiveSwitch } from "replo-utils/lib/misc";

import { useShopifyIntegrationConnect } from "./useShopifyIntegrationConnect";

type DesignPattern = "inline" | "popover" | "modal";

const DESIGN_PATTERN_MAP: Record<string, DesignPattern> = {
  shopStyles: "inline",
  publish: "popover",
  preview: "popover",
  analytics: "modal",
  projectView: "modal",
  integrationHub: "modal",
  shopSettingsConnection: "modal",
};

const getDesignPattern = (
  cookieType: PendingShopifyIntegrationCookie["type"],
): DesignPattern => {
  return DESIGN_PATTERN_MAP[cookieType] ?? "inline";
};

export const ConnectShopifyCallout: React.FC<{
  cookieValue: Exclude<
    PendingShopifyIntegrationCookie,
    | { type: "onboarding" }
    | { type: "noShopifyErrorModal" }
    | { type: "projectSettings" }
    | { type: "newProject" }
  >;
  className?: string;
  onCancel?: () => void; // For popover design pattern
  suppressContent?: boolean; // To hide title/description when parent has its own
}> = ({ cookieValue, className, onCancel, suppressContent = false }) => {
  const subscriptionTier = useSubscriptionTier();
  const designPattern = getDesignPattern(cookieValue.type);

  const detailMessage = exhaustiveSwitch({ type: cookieValue.type })({
    // Note (Evan, 2024-04-09): We only tell people to upgrade their account when they are on the free plan -
    // there are other cases where an upgrade may be required (e.g. the published pages limit has been reached)
    // but the logic of figuring that out is a little annoying, so we just account for the most obvious case here.
    publish: `To publish this page, add Shopify as an integration${subscriptionTier === "free" ? " and upgrade your account" : ""}.`,
    preview: `To preview this page, add Shopify as an integration.`,
    shopStyles: `To import saved styles from your Shopify theme, add Shopify as an integration.`,
    productPicker:
      "Replo allows you to access Shopify data dynamically in the editor. To access product data, add Shopify as an integration.",
    assets: "To add images via upload, add Shopify as an integration.",
    billingModal: null,
    shopSettingsConnection:
      "Replo will automatically sync your theme, products, and store info.",
    sections:
      "To pick a section from your Shopify theme, please connect a Shopify Store to this project.",
    projectView:
      // Note (Kevin 2025-05-06): "Replo allows you to access Shopify data dynamically in the editor by connecting your Shopify store." description is handled by the ShopifyButton.tsx Popover component
      null,
    analytics:
      "This allows Replo to start collecting data across all pages in your Shopify store.",
    integrationHub: null,
    abtesting: null,
  });

  const title = exhaustiveSwitch({ type: cookieValue.type })({
    publish: "Publish Page",
    preview: "Preview Page",
    productPicker: "Add Product Data",
    assets: "Upload Images",
    billingModal: null,
    shopSettingsConnection: "Shopify",
    sections: "Load Sections",
    shopStyles: "Import Saved Styles",
    // Note (Kevin 2025-05-06): "Connect Shopify Store" title is handled by the ShopifyButton.tsx Popover component
    projectView: null,
    analytics: "Connect Shopify to Track Analytics",
    integrationHub: null,
    abtesting: null,
  });

  let imageSrc = "/images/flows/integrate-shopify.png";

  if (designPattern === "modal") {
    if (cookieValue.type === "shopSettingsConnection") {
      imageSrc = "/images/flows/integrate-shopify-right-cropped.png";
    } else {
      imageSrc = "/images/flows/integrate-shopify-full.png";
    }
  }

  if (designPattern === "modal") {
    return (
      <div
        className={twMerge(
          "flex items-stretch justify-between -m-5",
          className,
        )}
      >
        <div className="w-65 flex flex-col gap-4 p-5 pr-1">
          {!suppressContent && title && (
            <h2 className="header-h3 text-default">{title}</h2>
          )}
          {!suppressContent && detailMessage && (
            <p className="text-base text-muted">{detailMessage}</p>
          )}

          <ConnectShopifyIntegrationCta
            cookieValue={cookieValue}
            showEndIcon={true}
            ctaText="Add Integration"
            layoutClassName="w-fit"
          />
        </div>

        <div className="relative flex-1 h-auto">
          <img
            src={imageSrc}
            alt="Integrate with Shopify"
            className="absolute inset-0 w-full h-full object-cover object-left select-none pointer-events-none"
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className={twMerge(
        "text-placeholder flex flex-col gap-4 py-2 text-base font-normal",
        (cookieValue.type === "publish" || cookieValue.type === "preview") &&
          "px-2",
        className,
      )}
    >
      {!suppressContent && (title || detailMessage) && (
        <div className="flex flex-col gap-2">
          {title && <h2 className="text-default font-semibold">{title}</h2>}
          {detailMessage && <p className="text-sm">{detailMessage}</p>}
        </div>
      )}

      {designPattern !== "popover" && (
        <ConnectShopifyIntegrationCta
          cookieValue={cookieValue}
          showEndIcon={true}
        />
      )}

      <img
        src={imageSrc}
        alt="Integrate with Shopify"
        className="my-2 block w-full"
      />

      {designPattern === "popover" && (
        <div className="flex flex-row gap-2 items-center">
          <Button variant="secondary" size="base" onClick={onCancel}>
            Cancel
          </Button>
          <ConnectShopifyIntegrationCta
            cookieValue={cookieValue}
            size="base"
            showEndIcon={true}
          />
        </div>
      )}
    </div>
  );
};

export const ConnectShopifyIntegrationCta: React.FC<{
  cookieValue: PendingShopifyIntegrationCookie;
  size?: ButtonSize;
  ctaText?: string;
  showEndIcon?: boolean;
  layoutClassName?: string;
}> = ({
  cookieValue,
  size = "base",
  ctaText = "Connect Shopify",
  showEndIcon = false,
  layoutClassName,
}) => {
  const workspaceId = useCurrentWorkspaceId();
  const currentProjectId = useCurrentProjectId();
  const projectIdFromCookie = exhaustiveSwitch(cookieValue)({
    onboarding: () => null,
    integrationHub: () => null,
    noShopifyErrorModal: ({ projectId }) => projectId,
    productPicker: ({ projectId }) => projectId,
    sections: ({ projectId }) => projectId,
    projectSettings: ({ projectId }) => projectId,
    newProject: ({ projectId }) => projectId,
    publish: ({ projectId }) => projectId,
    shopStyles: ({ projectId }) => projectId,
    preview: ({ projectId }) => projectId,
    assets: ({ projectId }) => projectId,
    billingModal: ({ projectId }) => projectId,
    shopSettingsConnection: ({ projectId }) => projectId,
    projectView: ({ projectId }) => projectId,
    analytics: ({ projectId }) => projectId,
    abtesting: ({ projectId }) => projectId,
  });
  const effectiveProjectId = projectIdFromCookie ?? currentProjectId;

  const { isLoading, handleConnect } = useShopifyIntegrationConnect({
    workspaceId,
    projectId: effectiveProjectId,
    cookieValue,
  });

  const designPattern = getDesignPattern(cookieValue.type);

  return (
    <Button
      variant="primary"
      size={size}
      layoutClassName={
        layoutClassName ?? (designPattern === "popover" ? undefined : "w-full")
      }
      onClick={(e) => {
        void handleConnect(e);
      }}
      isLoading={isLoading}
      disabled={isLoading}
      endEnhancer={showEndIcon ? <ArrowUpRight size={16} /> : undefined}
    >
      {ctaText}
    </Button>
  );
};
